/**
 * @file Arch height extraction from a Volumental foot-scan OBJ mesh.
 *
 * Ported from the standalone arch-tool.html (arch-extraction-c, surface-sampling
 * version of 2026-10-27), which mirrors the backend rig — ms-rig-pointcloud
 * Job.py findArchApexMethodC/B and its blender.blend helper objects. Fetches an
 * OBJ URL and measures plantar arch height on the mesh SURFACE: at each slice
 * from 40% to 44% of foot length, an 11-point strip on the medial side is
 * projected onto the mesh triangles along ±Z, its heights are averaged, and the
 * highest slice wins.
 *
 * The previous version sampled raw vertices only; on the ~484-vertex Volumental
 * web meshes it could rest on a single side-wall vertex and read ~3 mm high.
 * See docs/projects/foot-facts-web-port/FINDINGS-arch-measurement.md (Finding 1).
 */
(function () {
  'use strict';

  // Guard against multiple loads — this asset is included by more than one block.
  if (window.ArchHeightCalculator) return;

  // ── OBJ parser (vertices + triangles; ignores vn / vt) ───────────────────
  function parseOBJ(text) {
    const verts = [];
    const faces = [];
    for (const line of text.split('\n')) {
      if (line.startsWith('v ')) {
        const p = line.trim().split(/\s+/);
        const x = parseFloat(p[1]), y = parseFloat(p[2]), z = parseFloat(p[3]);
        if (!isNaN(x) && !isNaN(y) && !isNaN(z)) verts.push({ x, y, z });
      } else if (line.startsWith('f ')) {
        // Fan-triangulate; tokens may be "v", "v/vt" or "v/vt/vn" (1-based indices).
        const idx = line.trim().split(/\s+/).slice(1).map((s) => parseInt(s, 10) - 1);
        for (let i = 1; i < idx.length - 1; i++) faces.push([idx[0], idx[i], idx[i + 1]]);
      }
    }
    return { verts, faces };
  }

  // ── Normalise coords ─────────────────────────────────────────────────────
  // Volumental OBJ: Y = heel→toe length, Z = height off ground, X = width.
  // Values are in metres; scale to mm when the largest extent is < 2 m.
  function prepareVerts(rawVerts) {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    for (const v of rawVerts) {
      if (v.x < minX) minX = v.x; if (v.x > maxX) maxX = v.x;
      if (v.y < minY) minY = v.y; if (v.y > maxY) maxY = v.y;
      if (v.z < minZ) minZ = v.z; if (v.z > maxZ) maxZ = v.z;
    }
    const extX = maxX - minX, extY = maxY - minY, extZ = maxZ - minZ;
    const scale = (Math.max(extX, extY, extZ) < 2) ? 1000 : 1; // metres → mm

    // Length axis zeroed at the heel. Height stays raw — the OBJ origin is the
    // ground plane and the rig casts its rays from z = 0.
    const norm = rawVerts.map((v) => ({
      len: (v.y - minY) * scale,
      z:   v.z * scale,
      w:   v.x * scale,
    }));
    return { norm, footLength: extY * scale };
  }

  // ── Arch height algorithm (mirrors Job.py findArchApexMethodC/B) ─────────
  const ARCH_SEARCH_LENGTHS = [0.40, 0.41, 0.42, 0.43, 0.44];
  const PERC_WIDTH   = 0.25; // strip half-width = 25% of centerline→medial extent
  const PERC_CENTER  = 0.65; // strip centre at 65% from centerline to medial side
  const STRIP_POINTS = 11;   // evenly spaced points on the rig's horizontalHeightFinder line
                             // (per the rig notes in fmf361 rig/data-extraction-job.py)

  // The rig's verticalSlicer: the medial-most width where the mesh crosses the
  // plane len = y. Starts at 0 like Job.py's x_loc.
  function sliceMedialMax(norm, faces, y, side) {
    let medial = 0;
    for (const f of faces) {
      for (let k = 0; k < 3; k++) {
        const a = norm[f[k]], b = norm[f[(k + 1) % 3]];
        if ((a.len - y) * (b.len - y) > 0 || a.len === b.len) continue;
        const x = a.w + (y - a.len) / (b.len - a.len) * (b.w - a.w);
        if (side === 'left' ? x > medial : x < medial) medial = x;
      }
    }
    return medial;
  }

  // The rig's Shrinkwrap PROJECT along ±Z from z = 0: the surface hit nearest the
  // ground, i.e. the plantar surface where the ray also crosses the top of the foot.
  // Returns null when the ray misses the mesh.
  function projectZ(norm, faces, x, y) {
    let hit = null;
    for (const f of faces) {
      const A = norm[f[0]], B = norm[f[1]], C = norm[f[2]];
      const d = (B.len - C.len) * (A.w - C.w) + (C.w - B.w) * (A.len - C.len);
      if (Math.abs(d) < 1e-12) continue;
      const u = ((B.len - C.len) * (x - C.w) + (C.w - B.w) * (y - C.len)) / d;
      const v = ((C.len - A.len) * (x - C.w) + (A.w - C.w) * (y - C.len)) / d;
      const t = 1 - u - v;
      if (u < 0 || v < 0 || t < 0) continue;
      const z = u * A.z + v * B.z + t * C.z;
      if (hit === null || Math.abs(z) < Math.abs(hit)) hit = z;
    }
    return hit;
  }

  function calculateArchHeight(norm, faces, footLength, side) {
    let best = null;
    for (const percLength of ARCH_SEARCH_LENGTHS) {
      const y = footLength * percLength;
      const medialEdge = sliceMedialMax(norm, faces, y, side);
      const stripCenter = medialEdge * PERC_CENTER;
      const stripHalfWidth = medialEdge * PERC_WIDTH;

      // A ray that misses counts as height 0, as Blender leaves an unprojected
      // point where it started.
      let sum = 0, misses = 0;
      for (let i = 0; i < STRIP_POINTS; i++) {
        const x = stripCenter + stripHalfWidth * (-1 + 2 * i / (STRIP_POINTS - 1));
        const z = projectZ(norm, faces, x, y);
        if (z === null) misses++;
        else sum += z;
      }
      const avgHeight = sum / STRIP_POINTS;

      // Job.py keeps the first slice unless a later one is strictly higher
      // (its maxHeight starts at 0).
      if (avgHeight > (best ? best.archHeight : 0)) {
        best = { percLength, archHeight: avgHeight, misses };
      }
    }
    return best;
  }

  // ── Classification (delegates to footfacts — decision D10) ───────────────
  // The POC's original 0.025/0.08 bands came from an unimplemented note in the
  // arch-extraction readme and never matched the email's classifier. The email's
  // classifier (computeFootFacts, ported as foot-facts-calculator.js) is canonical,
  // so the class and the 0–10 percentile index both come from
  // window.FootFactsCalculator — that asset must be loaded before this one.
  // See docs/projects/foot-facts-web-port/HANDOFF-arch-height-calculator.md.
  function classify(archHeight, footLength) {
    if (!window.FootFactsCalculator) return null;
    return window.FootFactsCalculator.classifyArchHeight({ archHeight: archHeight, length: footLength });
  }

  function archPercentile(archHeight, footLength) {
    if (!window.FootFactsCalculator) return null;
    // computeFootFacts rounds dimensions to whole mm before deriving percentiles —
    // mirror that here so the values match the full pipeline exactly.
    return window.FootFactsCalculator.getFootPercentiles({
      archHeight: Math.round(archHeight),
      length: Math.round(footLength),
      width: 0, // only archHeightPercentile is read from the result
    }).archHeightPercentile;
  }

  // ── Public: fetch an OBJ URL and compute arch height for one foot ────────
  async function fromUrl(objUrl, side) {
    try {
      if (!objUrl) return { side, error: 'no mesh URL' };
      const res = await fetch(objUrl);
      if (!res.ok) return { side, error: 'fetch failed (HTTP ' + res.status + ')' };
      const text = await res.text();
      const { verts, faces } = parseOBJ(text);
      if (verts.length < 100) return { side, error: 'only ' + verts.length + ' vertices' };
      if (!faces.length) return { side, error: 'no faces (surface sampling needs a mesh)' };

      const { norm, footLength } = prepareVerts(verts);
      const best = calculateArchHeight(norm, faces, footLength, side);
      if (!best) return { side, error: 'no positive arch height at 40–44% of foot length' };

      const normalised = best.archHeight / footLength;
      const archHeightClass = classify(best.archHeight, footLength);
      return {
        side,
        footLength,
        archHeight: best.archHeight,
        normalised,
        archHeightClass,
        archHeightPercentile: archPercentile(best.archHeight, footLength),
        // Kept as an alias of archHeightClass for the existing POC notification code.
        label: archHeightClass,
        percLength: best.percLength,
        // Strip rays that missed the mesh at the chosen slice (each counted as 0 mm).
        stripMisses: best.misses,
      };
    } catch (err) {
      return { side, error: (err && err.message) ? err.message : String(err) };
    }
  }

  // Expose to the global window object (same pattern as size-calculator.js).
  window.ArchHeightCalculator = {
    fromUrl,
    parseOBJ,
    prepareVerts,
    calculateArchHeight,
    classify,
  };
})();
